import { useRef, useEffect } from 'react';
import MainFormContainer from '@/wrappers/MainFormContainer';
import PostcodeElement, { type PostcodeElementRef } from '@/elements/Postcode';
import { useRouter } from '@tanstack/react-router';
import { updateBasicInfo } from '@/utils/client-storage/localStorage';
import { GET_JOURNEY_PAGES } from '@/routes/journey.routes/index';
import imgSignature from '@/assets/img/Signature.svg';
import { LcpDisclaimer } from '@/components/LcpDisclaimer';
import { CustomerReviews } from '@/components/CustomerReviews';
import clsx from 'clsx';
import { clearAllSessionData } from '@/utils/functions';

const TOP_REVIEWS = [
  {
    id: 1,
    rating: 5,
    description:
      'I have always been very sceptical regarding money claims… My experience with Fair has been hassle free… overall a worry free process.',
    author: 'Dave Dunstall',
    isVerified: true,
  },
  {
    id: 2,
    rating: 5,
    description:
      'Started just to see if I qualified… they got back to me with 6 potential claims. Emailed every step of the way.',
    author: 'Michael',
    isVerified: true,
  },
  {
    id: 3,
    rating: 5,
    description:
      'Updates at every stage… absolutely pain free. Unlike others that promise so much and… fail to update you regarding your claim.',
    author: 'Diana Lane',
    isVerified: true,
  },
  {
    id: 4,
    rating: 5,
    description:
      'The process makes it extremely easy for people like me who have no records of Licence numbers / agreements.',
    author: 'GM',
    isVerified: true,
  },
];

export default function Postcode({
  homepageMode = false,
  hideAfterForm = false,
}: {
  homepageMode?: boolean;
  hideAfterForm?: boolean;
}) {
  const postcodeRef = useRef<PostcodeElementRef | null>(null);
  const router = useRouter();
  const urlParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  const utmSource = urlParams.get('utm_source');
  const isDM = utmSource && utmSource.includes('DM');

  const isMailDomain =
    typeof window !== 'undefined' && (window.location.hostname === 'localhost' || isDM);

  useEffect(() => {
    clearAllSessionData();
  }, []);

  const handleSubmit = async () => {
    const pcData = postcodeRef.current?.getData();
    const { postcode, selectedPostcode, addresses } = pcData ?? {
      postcode: '',
      selectedPostcode: '',
      addresses: [],
    };

    const finalPostcode = selectedPostcode || postcode;

    if (!finalPostcode || !postcodeRef.current?.validate()) {
      return;
    }

    updateBasicInfo({
      postcode: finalPostcode,
    });

    const destination = 'ADDRESS';
    const pages = GET_JOURNEY_PAGES() as any;

    if (pages && pages[destination]) {
      router.navigate({
        to: pages[destination].path,
        state: {
          prefetchedAddresses: addresses,
          postcode: finalPostcode,
        } as any,
      });
    } else {
      console.error('Address lookup page not found');
    }
  };

  const postCodeElementComp = (
    <form
      id="postcode-section"
      onSubmit={(e) => e.preventDefault()}
      className={clsx('w-full', !homepageMode && 'px-5')}
    >
      <PostcodeElement
        ref={postcodeRef}
        homepageMode={homepageMode}
        triggerQueryParams={true}
        title="Please enter your Current Address"
        onSubmit={handleSubmit}
      />
    </form>
  );

  const compIternal = (
    <>
      {/* Header Group */}
      <div className="w-full flex flex-col items-start gap-4">
        {homepageMode && isMailDomain && (
          <div className="inline-flex items-center gap-2 bg-white border border-primary/30 rounded-full px-4 py-2">
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-primary font-semibold text-body lg:text-body">
              Thanks for scanning &mdash; welcome.
            </span>
          </div>
        )}

        <h1 className="text-hero font-black text-text-main leading-[1.05] tracking-tight text-left w-full">
          Let&rsquo;s see if you <span className="text-primary">could be owed</span> a refund.
        </h1>
        <div className="text-text-main text-subtitle lg:text-body font-medium leading-snug -mt-1 w-full text-left">
          Pop your postcode to see if you could be owed{' '}
          <span className="text-primary font-bold text-subtitle">&pound;1,658*</span>
        </div>
      </div>

      {/* Input & Trust Group - tight gap */}
      <div className="w-full flex flex-col items-start gap-4">
        {postCodeElementComp}

        {/* Trust & Footer Group */}
        <div className="w-full flex flex-col items-start gap-4">
          <div className="flex items-center justify-center gap-2 lg:gap-5 text-[clamp(10px,4vw,16px)] font-semibold text-text-main w-full whitespace-nowrap">
            <span>🔒 Secure &bull; Takes 60 seconds &bull; No credit hit</span>
          </div>

          {homepageMode && (
            <div className="w-full">
              <CustomerReviews reviews={TOP_REVIEWS} variant="carousel" minimal showDots={true} />
            </div>
          )}

          {homepageMode && (
            <div className="w-full flex flex-col items-start gap-4 pt-6 border-t border-dashed border-primary/30">
              <div className="flex items-center gap-3.5">
                <img
                  src={imgSignature}
                  alt="Signature"
                  width={40}
                  height={40}
                  className="w-10 h-10 flex-shrink-0"
                />
                <div className="flex flex-col leading-tight">
                  <span className="text-text-main font-bold text-body tracking-tight">
                    Scott Mapleback
                  </span>
                  <span className="text-text-muted text-body font-medium">
                    Head of Motor Finance Claims
                  </span>
                  <span className="text-primary font-bold text-micro mt-0.5 uppercase tracking-wide">
                    FCA FRN 830181
                  </span>
                </div>
              </div>

              <LcpDisclaimer />
            </div>
          )}

          {!homepageMode && (
            <div className="text-page-desc px-5">
              Check your eligibility today — simple, and clear
            </div>
          )}
        </div>
      </div>
    </>
  );

  const getProgress = () => {
    if (homepageMode) return 0;
    return 20;
  };

  return (
    <MainFormContainer
      progress={getProgress()}
      hideAfterForm={hideAfterForm}
      currentStep={homepageMode ? undefined : 1}
      totalSteps={homepageMode ? undefined : 5}
      isHome={homepageMode}
      contentClassName={`p-5 lg:pb-20 flex flex-col items-start gap-6 ${
        homepageMode ? 'lg:px-0' : 'lg:px-20'
      }`}
    >
      {compIternal}
    </MainFormContainer>
  );
}
